import React from 'react';
import {
  BaseButton,
  FilteredTable,
  Gutter,
  Icon,
  Text,
  TextSearchTableFilter,
} from '@common/components';
import { convertToType } from '@scheduling/services';
import { usePaginationHelper, useQuery, useNavigate } from '@common/hooks';
import { PAGINATED_WORKFORCE_GROUP_ORGANIZATION_BUSINESS_CONFIGS_QUERY } from '@scheduling/graphql';
import {
  InputMaybe,
  OrderDirection,
  WfgOrganizationBusinessConfigFragment,
  WorkforceGroupOrganizationBusinessConfigOrderField,
} from '@common/graphql';
import { buildDialogUrl } from '@common/services';
import { Dropdown } from 'antd';

export function AdminWorkforceGroupLocations({
  workforceGroupId,
}: {
  workforceGroupId: string;
}) {
  const LOCALSTORAGE_FILTER_KEY = 'GroupFacilitiesFilters';
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCALSTORAGE_FILTER_KEY,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'BUSINESS_NAME',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'BUSINESS_NAME',
      earliestAdvanceTime: 'EARLIEST_ADVANCE_TIME',
      openShiftsVisibilityDelayHours: 'OPEN_SHIFTS_VISIBILITY_DELAY_HOURS',
      useDefaultSettings: 'USE_DEFAULT_SETTINGS',
    },
  });

  const { loading, data } = useQuery(
    PAGINATED_WORKFORCE_GROUP_ORGANIZATION_BUSINESS_CONFIGS_QUERY,
    {
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderDirection: paginationVariables.orderDirection as
          | InputMaybe<OrderDirection>
          | undefined,
        orderField: paginationVariables.orderField as
          | InputMaybe<WorkforceGroupOrganizationBusinessConfigOrderField>
          | undefined,
        filters: { ...filters, workforceGroup: [workforceGroupId] },
      },
      skip: !workforceGroupId,
    }
  );

  const navigate = useNavigate();

  const displayDays = (hours: number) => {
    const days = convertToType(String(hours), 'hours', 'days');

    return Number.isInteger(days) ? days : days.toFixed(1);
  };

  const displayConfig = (config: WfgOrganizationBusinessConfigFragment) => {
    if (config.useDefaultSettings && config.workforceGroup?.defaultConfig) {
      return config.workforceGroup.defaultConfig;
    }

    return config;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'organizationBusiness',
      key: 'businessName',
      width: '40%',
      render: (
        organizationBusiness: WfgOrganizationBusinessConfigFragment['organizationBusiness']
      ) => {
        return <Text>{organizationBusiness?.name}</Text>;
      },
      sorter: true,
      filtered: !!filters.business_name || false,
      filteredValue: filters.business_name || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      title: 'Default Settings',
      dataIndex: 'useDefaultSettings',
      key: 'useDefaultSettings',
      width: '10%',
      render: (useDefaultSettings: boolean) => {
        if (!useDefaultSettings) {
          return '';
        }
        return (
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            className="default-setting-on"
          >
            <Icon icon="check" size="lg" color="steelDarkest" />
          </div>
        );
      },
      sorter: true,
    },
    {
      title: 'Advance Shift Visibility',
      key: 'earliestAdvanceTime',
      dataIndex: 'earliestAdvanceTime',
      width: '22%',
      render: (
        _earliestAdvanceTime: number,
        config: WfgOrganizationBusinessConfigFragment
      ) => {
        const { earliestAdvanceTime, gateEarliestAdvanceTime } =
          displayConfig(config);
        if (
          !earliestAdvanceTime ||
          earliestAdvanceTime === 0 ||
          !gateEarliestAdvanceTime
        ) {
          return '';
        }

        return (
          <Text>
            {displayDays(earliestAdvanceTime)}{' '}
            {earliestAdvanceTime === 1 ? 'day' : 'days'}
          </Text>
        );
      },
      sorter: true,
    },
    {
      title: 'Schedule Release Delay',
      dataIndex: 'openShiftsVisibilityDelayHours',
      width: '22%',
      key: 'openShiftsVisibilityDelayHours',
      render: (
        _openShiftsVisibilityDelayHours: number,
        config: WfgOrganizationBusinessConfigFragment
      ) => {
        const { openShiftsVisibilityDelayHours, gateOpenShiftsVisibility } =
          displayConfig(config);
        if (
          !openShiftsVisibilityDelayHours ||
          openShiftsVisibilityDelayHours === 0 ||
          !gateOpenShiftsVisibility
        ) {
          return '';
        }

        return (
          <Text>
            {displayDays(openShiftsVisibilityDelayHours)}{' '}
            {openShiftsVisibilityDelayHours === 1 ? 'day' : 'days'}
          </Text>
        );
      },
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '6%',
      render: (_: unknown, config: WfgOrganizationBusinessConfigFragment) => {
        const wfgOrgBusinessUpdateDrawerUrl = buildDialogUrl(
          'workforce-group-org-business-update-drawer',
          {
            props: { wfgConfigId: config.id },
          }
        );

        const items = [
          {
            key: 'edit',
            label: 'Edit',
            onClick: () => {
              navigate(wfgOrgBusinessUpdateDrawerUrl);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <BaseButton type="link">
              <Icon
                kind="regular"
                icon="verticalEllipsis"
                size="md"
                color="elm"
              />
            </BaseButton>
          </Dropdown>
        );
      },
    },
  ];

  const dataSource =
    data?.paginatedWorkforceGroupOrganizationBusinessConfigs
      .workforceGroupOrganizationBusinessConfigs || [];

  const totalCount =
    data?.paginatedWorkforceGroupOrganizationBusinessConfigs?.totalCount || 0;

  return (
    <>
      <Gutter>
        <FilteredTable
          rowKey="id"
          columns={columns}
          loading={loading}
          dataSource={dataSource}
          onFilterChange={filterChangeHandler}
          pagination={{
            ...paginationVariables,
            total: totalCount,
          }}
          localStorageFilterKey={localStorageFilterKey}
          onChange={tableChangeHandler}
          filters={filters}
          scroll={{ x: 'max-content' }}
        />
      </Gutter>
    </>
  );
}
